// Holding page shown to public visitors while the app is in private beta.
// Renders only at "/" on cogniprint.io (gated by hostname in App.tsx). All
// other public routes redirect here while the gate is active. Authenticated
// routes still work for users who navigate directly to /login or /admin-login.
//
// Mini-landing layout: hero + waitlist form anchored by the particle orb,
// then "How it works" 3-step, "Why it's different" 4-card grid, mini FAQ,
// and a closing waitlist CTA. Copy lifted from Landing.tsx for consistency.

import { useState, lazy, Suspense, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Sparkles,
  Check,
  Fingerprint,
  BookOpen,
  Eye,
  Brain,
  Zap,
  RefreshCw,
} from 'lucide-react';
import { addToWaitlist, isValidEmail } from '@shared/services/waitlist';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import { Aurora, FloatingParticles, SectionDivider } from '../components/ui/depth';
import { Accordion } from '../components/ui/Accordion';
import { THICK_TILE_STYLE } from '../components/dashboard/tileStyles';

const ParticleFingerprint = lazy(() =>
  import('../components/dashboard/ParticleFingerprint').then((m) => ({
    default: m.ParticleFingerprint,
  })),
);

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const steps = [
  {
    icon: Fingerprint,
    number: '01',
    overline: 'STEP 1',
    title: 'Discover how you learn',
    subtitle: 'The CogniPrint Diagnostic',
    body: 'Answer a short set of questions — about 2 minutes — and CogniPrint maps your learning fingerprint across 4 core dimensions. Upgrade to unlock all 8 for deeper personalisation.',
    accent: 'indigo' as const,
  },
  {
    icon: BookOpen,
    number: '02',
    overline: 'STEP 2',
    title: 'Upload your content',
    subtitle: 'Your textbook, your terms',
    body: 'Drop in the PDF, document, or audio you need to learn. CogniPrint extracts every key concept and builds a connected knowledge graph of how everything fits together.',
    accent: 'mixed' as const,
  },
  {
    icon: Sparkles,
    number: '03',
    overline: 'STEP 3',
    title: 'Learn your way',
    subtitle: 'AI-powered. Personally yours.',
    body: 'Your content rebuilt for your CogniPrint. Visual learner? Concept maps first. Hands-on? Worked examples. Need depth? You get it. Professor Cogni adapts at every step.',
    accent: 'gold' as const,
  },
];

const features = [
  {
    icon: Eye,
    title: 'Your learning profile',
    body: 'Eight dimensions of how your brain processes, retains, and connects information. A living profile that shapes every learning experience CogniPrint creates for you.',
  },
  {
    icon: Brain,
    title: 'Professor Cogni',
    body: 'Your personal AI tutor. Knows your content, your profile, and where you are in the material. Ask him to explain differently, go deeper, or test you.',
  },
  {
    icon: Zap,
    title: 'Adaptive engine',
    body: 'Tracks how you engage as you study and quietly recalibrates your learning path. The experience keeps getting better the more you use it.',
  },
  {
    icon: RefreshCw,
    title: 'Bi-weekly recalibration',
    body: 'Every two weeks your profile recalibrates against how you actually studied. A short monthly report shows what moved and what it means.',
  },
];

const miniFaq = [
  {
    question: 'Is my content safe and private?',
    answer:
      'Yes. Your uploaded content is processed by AI to generate your learning experience but is not stored permanently or used to train AI models. CogniPrint is built with privacy by design — POPIA and GDPR compliant.',
  },
  {
    question: 'Is there a free tier?',
    answer:
      'Yes — your quick CogniPrint profile is free, always. The 2-minute diagnostic costs nothing and gives you genuine insight into how you learn across 4 core dimensions. Plans for the full experience start at $9.99/month.',
  },
  {
    question: 'Does it work on mobile?',
    answer:
      'Yes. CogniPrint is fully responsive on mobile browsers, and a native app is in development.',
  },
];

function WaitlistForm({ id, compact = false }: { id?: string; compact?: boolean }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<SubmitState>({ kind: 'idle' });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state.kind === 'submitting' || state.kind === 'success') return;
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setState({ kind: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    setState({ kind: 'submitting' });
    try {
      await addToWaitlist(trimmed, 'coming-soon');
      setState({ kind: 'success' });
      setEmail('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Try again.';
      setState({ kind: 'error', message });
    }
  }

  return (
    <motion.div
      id={id}
      className={`relative overflow-hidden rounded-2xl border border-indigo-400/20 mx-auto text-left ${
        compact ? 'p-4 sm:p-5 max-w-md' : 'p-5 sm:p-7 max-w-lg'
      }`}
      style={THICK_TILE_STYLE}
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <p className="text-[10px] font-body font-bold tracking-[0.18em] uppercase text-gold-400 mb-2 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-gold-500 animate-pulse" />
        Join the waitlist
      </p>
      {!compact && (
        <h2 className="font-heading font-semibold text-lg sm:text-xl text-content mb-4">
          Be first in line when we open up.
        </h2>
      )}

      {state.kind === 'success' ? (
        <div className="flex items-center gap-2.5 text-sm font-body text-success">
          <Check size={16} className="shrink-0" />
          <span>You're on the list. We'll be in touch soon.</span>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (state.kind === 'error') setState({ kind: 'idle' });
            }}
            disabled={state.kind === 'submitting'}
            className="flex-1 px-4 py-2.5 rounded-lg bg-dark-bg/60 border border-dark-border text-content text-sm font-body placeholder:text-content-dim focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40 transition-colors disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={state.kind === 'submitting'}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 text-white text-sm font-semibold shadow-lg shadow-indigo-500/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
          >
            {state.kind === 'submitting' ? 'Joining…' : (
              <>Notify me <ArrowRight size={14} /></>
            )}
          </button>
        </form>
      )}

      {state.kind === 'error' ? (
        <p className="mt-2 text-xs font-body text-error">{state.message}</p>
      ) : null}

      <p className="mt-4 text-[11px] font-body text-content-dim">
        No spam. Just one note when CogniPrint opens up.
      </p>
    </motion.div>
  );
}

export function ComingSoonPage() {
  return (
    <div className="min-h-screen bg-dark-bg relative overflow-hidden">
      <RidgeTexture opacity="opacity-[0.07]" />
      <Aurora palette="mixed" intensity={0.6} />
      <FloatingParticles />

      {/* Logo */}
      <header className="relative z-10 px-6 sm:px-10 pt-8">
        <img
          src="/logo-horizontal.png"
          alt="CogniPrint"
          className="h-10 sm:h-12 w-auto"
        />
      </header>

      {/* ── HERO ────────────────────────────────────────── */}
      <section className="relative z-10 px-6 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-pill text-xs font-semibold bg-gold-500/12 text-gold-400 border border-gold-500/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Sparkles size={12} /> Private beta · launching soon
          </motion.span>

          <motion.h1
            className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl tracking-tight mb-5 leading-[1.05]"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            CogniPrint{' '}
            <span className="bg-gradient-to-r from-indigo-400 via-[#A594F9] to-gold-500 bg-clip-text text-transparent">
              is coming.
            </span>
          </motion.h1>

          <motion.p
            className="text-content-secondary font-body text-base sm:text-lg leading-relaxed max-w-xl mx-auto mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
          >
            Learning, reimagined to be as unique as your fingerprint. AI-powered
            adaptive study built around how <em className="text-content not-italic">your</em> brain actually works.
          </motion.p>

          {/* Particle orb — small, anchoring the hero */}
          <motion.div
            className="flex justify-center mb-10"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            <Suspense fallback={null}>
              <ParticleFingerprint active size={180} canvasSize={500} />
            </Suspense>
          </motion.div>

          <WaitlistForm />
        </div>
      </section>

      <SectionDivider />

      {/* ── HOW IT WORKS ──────────────────────────────── */}
      <section className="relative z-10 px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center max-w-xl mx-auto mb-12"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block mb-4 px-3 py-1 rounded-pill text-xs font-semibold bg-indigo-500/12 text-indigo-300 border border-indigo-500/30">
              How it works
            </span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
              Three steps. Five minutes to your first one.
            </h2>
            <p className="text-content-secondary font-body">
              From a flat textbook to a learning experience built for your brain.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((step, i) => {
              const stepBorder =
                step.accent === 'gold'
                  ? 'border-gold-500/25'
                  : step.accent === 'mixed'
                    ? 'border-indigo-400/20'
                    : 'border-indigo-500/25';
              const stepNumberColor =
                step.accent === 'gold'
                  ? 'text-gold-500/30'
                  : step.accent === 'mixed'
                    ? 'text-indigo-300/30'
                    : 'text-indigo-400/30';
              const iconBg =
                step.accent === 'gold'
                  ? 'bg-gradient-to-br from-gold-400 to-gold-600'
                  : step.accent === 'mixed'
                    ? 'bg-gradient-to-br from-indigo-500 to-gold-500'
                    : 'bg-gradient-to-br from-indigo-500 to-indigo-700';
              return (
                <motion.div
                  key={step.number}
                  className={`relative overflow-hidden rounded-xl border ${stepBorder} p-6 h-full`}
                  style={THICK_TILE_STYLE}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                >
                  <div
                    className={`absolute top-2 right-3 text-[64px] font-heading font-extrabold ${stepNumberColor} leading-none select-none pointer-events-none tracking-tight`}
                  >
                    {step.number}
                  </div>
                  <div className={`relative w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${iconBg}`}>
                    <step.icon size={24} className="text-white" />
                  </div>
                  <p className="text-[10px] font-body font-bold tracking-[0.16em] uppercase text-content-muted mb-2">
                    {step.overline}
                  </p>
                  <h3 className="text-xl font-heading font-semibold mb-1">{step.title}</h3>
                  <p className="text-sm font-body text-gold-400 mb-3">{step.subtitle}</p>
                  <p className="text-content-secondary text-sm leading-relaxed font-body">{step.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* ── WHY IT'S DIFFERENT ──────────────────────────── */}
      <section className="relative z-10 px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center max-w-xl mx-auto mb-12"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block mb-4 px-3 py-1 rounded-pill text-xs font-semibold bg-gold-500/12 text-gold-400 border border-gold-500/30">
              What's inside
            </span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
              Built around how your brain actually works.
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                className="relative overflow-hidden rounded-xl border border-indigo-400/15 p-5"
                style={THICK_TILE_STYLE}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.45 }}
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-4">
                  <f.icon size={20} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-base mb-2">{f.title}</h3>
                <p className="text-sm font-body text-content-secondary leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* ── FAQ TEASER ───────────────────────────────────── */}
      <section className="relative z-10 px-6 py-16">
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold">A few quick answers</h2>
          </motion.div>
          <Accordion items={miniFaq} />
        </div>
      </section>

      <SectionDivider />

      {/* ── CLOSING CTA ─────────────────────────────────── */}
      <section className="relative z-10 px-6 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <motion.h2
            className="font-heading font-bold text-3xl sm:text-4xl mb-4 leading-tight"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Your brain has a fingerprint.
            <br />
            <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">
              Be first to use it.
            </span>
          </motion.h2>
          <p className="text-content-secondary font-body mb-8">
            CogniPrint opens up to early-access members soon. Drop your email and we'll send one note when it does.
          </p>
          <WaitlistForm compact />
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 sm:px-10 pb-6 pt-10 text-center">
        <p className="text-xs font-body text-content-dim">
          © {new Date().getFullYear()} CogniPrint · Built for how your brain works
        </p>
      </footer>
    </div>
  );
}
